import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Activity, MessageCircle, Mail, Globe, TrendingUp, Users,
  Wallet, Sparkles, Clock, CheckCircle2, AlertCircle,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 30000;

const formatRel = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const mins = Math.floor((Date.now() - d.getTime()) / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  } catch {
    return iso;
  }
};

const Pill = ({ children, tone = 'neutral' }) => {
  const tones = {
    ok: 'bg-emerald-100 text-emerald-700 border-emerald-300',
    warn: 'bg-amber-100 text-amber-700 border-amber-300',
    bad: 'bg-rose-100 text-rose-700 border-rose-300',
    neutral: 'bg-slate-100 text-slate-700 border-slate-300',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-bold tracking-wider uppercase border ${tones[tone]}`}>
      {children}
    </span>
  );
};

const Metric = ({ label, value, sub, accent = '#D4AF37' }) => (
  <div className="border border-white/10 bg-black/30 px-4 py-3">
    <p className="text-[10px] tracking-[0.18em] uppercase font-bold text-white/50">{label}</p>
    <p className="text-2xl font-bold text-white mt-1" style={{ color: accent }}>{value}</p>
    {sub && <p className="text-[11px] text-white/60 mt-1">{sub}</p>}
  </div>
);

export const AdminLiveConversionTile = () => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchPulse = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/sales/conversion-pulse`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
      setErr(null);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to fetch');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPulse();
    const id = setInterval(fetchPulse, POLL_MS);
    return () => clearInterval(id);
  }, [fetchPulse]);

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-[#0A0A0C] via-[#1A1A1A] to-[#0A0A0C] border-l-4 border-[#D4AF37] p-6 text-white/60 text-sm">
        Loading live conversion pulse…
      </div>
    );
  }
  if (err) {
    return (
      <div className="bg-rose-50 border-l-4 border-rose-400 p-4 text-sm text-rose-700">
        Pulse error: {err}
      </div>
    );
  }
  if (!data) return null;

  const {
    wa_burst, email_burst, email_scraper, signups,
    trials_and_paid, stripe, wa_delivery_24h, recent_claims,
  } = data;

  return (
    <div
      data-testid="admin-conversion-pulse"
      className="relative bg-gradient-to-br from-[#0A0A0C] via-[#1A1A1A] to-[#0A0A0C] border-l-4 border-[#D4AF37] overflow-hidden mb-6"
    >
      <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-[#D4AF37] blur-3xl opacity-10 pointer-events-none" />

      <div className="relative px-6 py-5">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div>
            <p className="text-[10px] tracking-[0.22em] uppercase text-[#D4AF37] font-bold mb-1">
              Live Revenue Pulse
            </p>
            <h2 className="font-['Playfair_Display'] text-2xl text-white">
              Money Flow <span className="text-[#D4AF37]">Right Now</span>
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <Pill tone={stripe?.livemode ? 'ok' : 'warn'}>
              {stripe?.livemode ? 'LIVE MODE' : 'TEST MODE'}
            </Pill>
            <span className="text-[10px] text-white/40">refreshes every 30s</span>
          </div>
        </div>

        {/* Top row — money + signups */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          <Metric
            label="Stripe Available"
            value={`£${(stripe?.available_gbp || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}`}
            sub={stripe?.error ? `⚠ ${stripe.error}` : 'GBP livemode'}
            accent="#22C55E"
          />
          <Metric
            label="MRR Estimate"
            value={`£${(trials_and_paid?.mrr_estimate_gbp || 0).toLocaleString('en-GB')}`}
            sub={`${trials_and_paid?.paid_active || 0} active subs`}
          />
          <Metric
            label="Signups 24h"
            value={signups?.last_24h || 0}
            sub={`${signups?.last_1h || 0} in last hour`}
            accent="#60A5FA"
          />
          <Metric
            label="Active Trials"
            value={trials_and_paid?.trials_active || 0}
            sub="14-day Verified Partner"
          />
        </div>

        {/* Outreach engines row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-4">
          {/* WA burst */}
          <div className="border border-[#25D366]/30 bg-[#25D366]/5 p-4">
            <div className="flex items-center gap-2 mb-2">
              <MessageCircle className="h-4 w-4 text-[#25D366]" />
              <p className="text-xs tracking-wider uppercase font-bold text-white">WhatsApp Burst</p>
              <Pill tone={wa_burst?.running ? 'ok' : 'neutral'}>
                {wa_burst?.running ? 'RUNNING' : 'IDLE'}
              </Pill>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div>
                <p className="text-xl font-bold text-emerald-400">{wa_burst?.ok || 0}</p>
                <p className="text-[10px] text-white/50 uppercase">Delivered</p>
              </div>
              <div>
                <p className="text-xl font-bold text-amber-300">{wa_burst?.remaining || 0}</p>
                <p className="text-[10px] text-white/50 uppercase">Queued</p>
              </div>
              <div>
                <p className="text-xl font-bold text-slate-400">{wa_burst?.skipped_landline || 0}</p>
                <p className="text-[10px] text-white/50 uppercase">Skipped LL</p>
              </div>
            </div>
            <p className="text-[10px] text-white/40 mt-2">
              Last send {formatRel(wa_burst?.last_at)}
              {wa_burst?.stopped_reason && ` · ${wa_burst.stopped_reason}`}
            </p>
          </div>

          {/* Email burst */}
          <div className="border border-[#60A5FA]/30 bg-[#60A5FA]/5 p-4">
            <div className="flex items-center gap-2 mb-2">
              <Mail className="h-4 w-4 text-[#60A5FA]" />
              <p className="text-xs tracking-wider uppercase font-bold text-white">Email Campaign</p>
              <Pill tone={email_burst?.running ? 'ok' : email_burst?.stopped_reason === 'complete' ? 'ok' : 'neutral'}>
                {email_burst?.running ? 'RUNNING' : email_burst?.stopped_reason === 'complete' ? 'DONE' : 'IDLE'}
              </Pill>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div>
                <p className="text-xl font-bold text-emerald-400">{email_burst?.ok || 0}</p>
                <p className="text-[10px] text-white/50 uppercase">Sent</p>
              </div>
              <div>
                <p className="text-xl font-bold text-rose-400">{email_burst?.failed || 0}</p>
                <p className="text-[10px] text-white/50 uppercase">Failed</p>
              </div>
              <div>
                <p className="text-xl font-bold text-slate-300">{email_burst?.total_pool || 0}</p>
                <p className="text-[10px] text-white/50 uppercase">Pool</p>
              </div>
            </div>
            <p className="text-[10px] text-white/40 mt-2 truncate">
              {email_burst?.campaign_id || '—'}
            </p>
          </div>

          {/* Email scraper */}
          <div className="border border-[#D4AF37]/30 bg-[#D4AF37]/5 p-4">
            <div className="flex items-center gap-2 mb-2">
              <Globe className="h-4 w-4 text-[#D4AF37]" />
              <p className="text-xs tracking-wider uppercase font-bold text-white">Email Scraper</p>
              <Pill tone={email_scraper?.running ? 'ok' : 'neutral'}>
                {email_scraper?.running ? 'CRAWLING' : 'IDLE'}
              </Pill>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div>
                <p className="text-xl font-bold text-emerald-400">{email_scraper?.emails_found || 0}</p>
                <p className="text-[10px] text-white/50 uppercase">Found</p>
              </div>
              <div>
                <p className="text-xl font-bold text-slate-300">{email_scraper?.scanned || 0}</p>
                <p className="text-[10px] text-white/50 uppercase">Scanned</p>
              </div>
              <div>
                <p className="text-xl font-bold text-[#D4AF37]">{email_scraper?.hit_rate_pct || 0}%</p>
                <p className="text-[10px] text-white/50 uppercase">Hit Rate</p>
              </div>
            </div>
          </div>
        </div>

        {/* WA delivery & recent claims */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <div className="border border-white/10 bg-black/30 p-4">
            <div className="flex items-center gap-2 mb-2">
              <Activity className="h-4 w-4 text-emerald-400" />
              <p className="text-xs tracking-wider uppercase font-bold text-white">WA Delivery (24h)</p>
              {!wa_delivery_24h?.webhook_active && (
                <Pill tone="warn">WEBHOOK OFF</Pill>
              )}
            </div>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div>
                <p className="text-2xl font-bold text-white">{wa_delivery_24h?.sent || 0}</p>
                <p className="text-[10px] text-white/50 uppercase">Sent</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-emerald-400">{wa_delivery_24h?.delivered || 0}</p>
                <p className="text-[10px] text-white/50 uppercase">Delivered</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-[#D4AF37]">{wa_delivery_24h?.read || 0}</p>
                <p className="text-[10px] text-white/50 uppercase">Read</p>
              </div>
            </div>
            {!wa_delivery_24h?.webhook_active && (
              <p className="text-[10px] text-amber-300 mt-2 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                Configure Meta webhook to populate delivery + read receipts.
              </p>
            )}
          </div>

          <div className="border border-white/10 bg-black/30 p-4">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="h-4 w-4 text-[#D4AF37]" />
              <p className="text-xs tracking-wider uppercase font-bold text-white">Recent Claims</p>
            </div>
            {(recent_claims || []).length === 0 ? (
              <p className="text-xs text-white/40 italic mt-2">
                No claims yet. First WA + email burst recipients typically convert 2-24h after send.
              </p>
            ) : (
              <ul className="space-y-1.5 mt-1">
                {recent_claims.slice(0, 5).map((c, i) => (
                  <li
                    key={`${c.at}-${i}-${c.name}`}
                    className="flex items-center justify-between text-xs text-white/80"
                  >
                    <span className="truncate flex-1">
                      <CheckCircle2 className="inline h-3 w-3 text-emerald-400 mr-1" />
                      {c.name} <span className="text-white/40">— {c.city || 'unknown'}</span>
                    </span>
                    <span className="text-white/40 text-[10px] ml-2 whitespace-nowrap">
                      {formatRel(c.at)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLiveConversionTile;
